import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function JoinDSA() {
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    city: "",
    experience: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for field on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Full name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Please enter your full name";
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = "Name must be at least 2 characters";
    }

    // Phone validation (Indian 10-digit mobile number)
    const phoneClean = formData.phone.trim().replace(/\D/g, "");
    if (!formData.phone.trim()) {
      newErrors.phone = "Please enter your mobile number";
    } else if (!/^[6-9]\d{9}$/.test(phoneClean)) {
      newErrors.phone = "Enter a valid 10-digit mobile number";
    }

    // City validation
    if (!formData.city.trim()) {
      newErrors.city = "Please enter your city";
    }

    // Experience validation
    if (!formData.experience.trim()) {
      newErrors.experience = "Please enter your years of experience";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields properly.", {
        position: "bottom-right",
        theme: "dark",
        autoClose: 3500,
      });
      return;
    }

    setIsSubmitting(true);

    // Simulate async submission
    setTimeout(() => {
      setIsSubmitting(false);
      toast.success(
        `Thank you ${formData.fullName.trim()}! We will call you within 1 working day to finish KYC.`,
        {
          position: "bottom-right",
          theme: "dark",
          autoClose: 5000,
        }
      );

      // Reset form
      setFormData({
        fullName: "",
        phone: "",
        city: "",
        experience: "",
      });
      setErrors({});
    }, 600);
  };

  return (
    <section id="join-as-dsa" className="overflow-hidden bg-[#191e1c] section-spacing">
      <div className="section-container">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14 xl:gap-16">
          {/* ================= LEFT CONTENT ================= */}
          <div className="lg:col-span-5">
            {/* Label */}
            <p className="font-jakarta text-[11px] font-medium tracking-[0.34em] text-[var(--mint-green)] sm:text-[12px]">
              JOIN AS A DSA
            </p>

            {/* Heading */}
            <h2 className="mt-4 font-bricolage text-[36px] font-semibold leading-[1.08] tracking-[-1px] text-white sm:mt-5 sm:text-[44px] md:text-[48px] lg:text-[52px]">
              Start submitting this week.
            </h2>

            {/* Description */}
            <p className="mt-4 font-jakarta text-[15px] leading-[1.6] text-[#8a9893] sm:mt-5 sm:text-[16px]">
              Fill this in. We call back within one working day to finish KYC.
            </p>
          </div>

          {/* ================= RIGHT FORM CARD ================= */}
          <div className="lg:col-span-7">
            <div className="relative overflow-hidden rounded-[24px] border border-[#2EE5A2]/25 bg-[#141816] p-6 shadow-2xl sm:rounded-[28px] sm:p-8 md:p-10">
              <form onSubmit={handleSubmit} noValidate>
                {/* 2x2 Grid */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
                  {/* Full Name */}
                  <div>
                    <label
                      htmlFor="fullName"
                      className="block font-jakarta text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7d8b85] sm:text-[11px]"
                    >
                      FULL NAME
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Your name"
                      className={`mt-2 w-full rounded-xl border bg-[#1e2422] px-4 py-3.5 font-jakarta text-[14px] text-white placeholder:text-[#52605b] sm:text-[15px] focus:outline-none transition-colors ${
                        errors.fullName
                          ? "border-red-500/80 focus:border-red-500"
                          : "border-white/10 focus:border-[var(--mint-green)]"
                      }`}
                    />
                    {errors.fullName && (
                      <p className="mt-1.5 font-jakarta text-[12px] text-red-400">
                        {errors.fullName}
                      </p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="phone"
                      className="block font-jakarta text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7d8b85] sm:text-[11px]"
                    >
                      PHONE
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="10–digit mobile"
                      maxLength={10}
                      className={`mt-2 w-full rounded-xl border bg-[#1e2422] px-4 py-3.5 font-jakarta text-[14px] text-white placeholder:text-[#52605b] sm:text-[15px] focus:outline-none transition-colors ${
                        errors.phone
                          ? "border-red-500/80 focus:border-red-500"
                          : "border-white/10 focus:border-[var(--mint-green)]"
                      }`}
                    />
                    {errors.phone && (
                      <p className="mt-1.5 font-jakarta text-[12px] text-red-400">
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  {/* City */}
                  <div>
                    <label
                      htmlFor="city"
                      className="block font-jakarta text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7d8b85] sm:text-[11px]"
                    >
                      CITY
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="e.g. Pune"
                      className={`mt-2 w-full rounded-xl border bg-[#1e2422] px-4 py-3.5 font-jakarta text-[14px] text-white placeholder:text-[#52605b] sm:text-[15px] focus:outline-none transition-colors ${
                        errors.city
                          ? "border-red-500/80 focus:border-red-500"
                          : "border-white/10 focus:border-[var(--mint-green)]"
                      }`}
                    />
                    {errors.city && (
                      <p className="mt-1.5 font-jakarta text-[12px] text-red-400">
                        {errors.city}
                      </p>
                    )}
                  </div>

                  {/* Experience */}
                  <div>
                    <label
                      htmlFor="experience"
                      className="block font-jakarta text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7d8b85] sm:text-[11px]"
                    >
                      EXPERIENCE
                    </label>
                    <input
                      type="text"
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      placeholder="Years in lending"
                      className={`mt-2 w-full rounded-xl border bg-[#1e2422] px-4 py-3.5 font-jakarta text-[14px] text-white placeholder:text-[#52605b] sm:text-[15px] focus:outline-none transition-colors ${
                        errors.experience
                          ? "border-red-500/80 focus:border-red-500"
                          : "border-white/10 focus:border-[var(--mint-green)]"
                      }`}
                    />
                    {errors.experience && (
                      <p className="mt-1.5 font-jakarta text-[12px] text-red-400">
                        {errors.experience}
                      </p>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="mt-6 sm:mt-8">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex h-[50px] items-center justify-center rounded-full bg-[var(--mint-green)] px-8 font-jakarta text-[15px] font-semibold text-[#071b17] shadow-lg shadow-[var(--mint-green)]/20 transition-all duration-200 hover:brightness-105 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 cursor-pointer sm:h-[54px] sm:px-9"
                  >
                    {isSubmitting ? "Submitting..." : "Join as a DSA"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* React Toast Container */}
      <ToastContainer />
    </section>
  );
}

export default JoinDSA;
